import type { ReactNode } from 'react'
import type { IconComponent } from './icon.types'

/**
 * The three things every list page has to render besides its data: nothing yet,
 * still loading, and it went wrong. Shared so all of them look the same
 * everywhere — from Tailwind UI's feedback/empty-states and a skeleton in the
 * house style.
 */

type EmptyStateProps = {
  title: string
  description?: string
  /** The outline set reads better at this size. */
  icon?: IconComponent
  action?: ReactNode
}

export const EmptyState = ({ title, description, icon: Icon, action }: EmptyStateProps) => (
  <div className="px-6 py-12 text-center">
    {Icon && <Icon aria-hidden className="mx-auto size-10 text-shell-ink-muted" />}
    <h3 className="mt-2 text-sm font-semibold text-shell-ink">{title}</h3>
    {description && <p className="mt-1 text-sm text-shell-ink-secondary">{description}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
)

/**
 * Placeholder rows while a list loads. Labelled rather than silent: a screen
 * reader gets "Loading X" instead of nothing at all, which is why the label is
 * a required prop.
 */
export const SkeletonRows = ({ label, rows = 3 }: { label: string; rows?: number }) => (
  <div aria-label={label} aria-busy="true" className="space-y-3 px-4 py-4 sm:px-6">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex items-center gap-x-4">
        <div className="h-4 flex-1 animate-pulse rounded bg-shell-surface-muted" />
        <div className="h-4 w-24 animate-pulse rounded bg-shell-surface-muted" />
      </div>
    ))}
  </div>
)

/**
 * A failed load, with the retry the user needs. Distinct from FeedbackCard,
 * which carries a *message* about something the user just did — this one
 * replaces content that isn't there.
 */
export const LoadError = ({
  message,
  retryLabel,
  onRetry,
}: {
  message: string
  retryLabel: string
  onRetry: () => void
}) => (
  <div className="px-6 py-10 text-center">
    <p className="text-sm text-shell-danger-ink">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-4 inline-flex items-center rounded-md bg-shell-surface px-3 py-2 text-sm font-semibold text-shell-ink shadow-xs inset-ring inset-ring-shell-border hover:bg-shell-surface-muted"
    >
      {retryLabel}
    </button>
  </div>
)
